import './AddTeamMemberList.css';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery } from 'src/services';
import { useGetUsersAllQuery } from 'src/services/usersService';
import { Checkbox } from 'src/components/ui/checkbox';
import { Separator } from 'src/components/ui/separator';
import { Controller, useForm } from 'react-hook-form';
import { Button } from 'src/components/ui/button';
import type { User } from 'src/services/usersService';

export const AddTeamMemberList = ({
  onSubmit,
}: {
  // Called with the ids of the users picked from the account.
  onSubmit?: (request: { users: number[] }) => void;
}) => {
  const { data: users } = useGetUsersAllQuery();
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      teamMembers: [],
    },
  });
  const teamMembersValue = watch('teamMembers');
  useEffect(() => {
    if (currentProject && users) {
      const members = users.filter((user) => {
        const userInProject = currentProject.team_members.some(
          (teamMember) => teamMember.user.id === user.id,
        );

        return !userInProject;
      });

      setTeamMembers(members);
    }
  }, [currentProject, users]);

  const submit = (data: { teamMembers: number[] }) => {
    if (!data.teamMembers.length) {
      return;
    }

    const request = {
      users: data.teamMembers,
    };

    onSubmit?.(request);
  };

  const onCheckedChange = (
    checked: boolean | 'indeterminate',
    onChange: (ids: number[]) => void,
    teamMember: Pick<User, 'id'>,
  ) => {
    let value;

    if (checked) {
      value = [...teamMembersValue, teamMember.id];
    } else {
      value = teamMembersValue.filter((itemId) => itemId !== teamMember.id);
    }

    onChange(value);
  };

  return (
    <form id="add-team-member-list-form" onSubmit={handleSubmit(submit)}>
      <ul className={'add-team-member-list'}>
        <Controller
          control={control}
          name={'teamMembers'}
          render={({ field: { onChange, value } }) => {
            return (
              <>
                {teamMembers.map((teamMember, index, array) => {
                  return (
                    <li className={'add-team-member-list-item'} key={teamMember.id}>
                      <label htmlFor={`${teamMember.id}-team-member`}>
                        <Checkbox
                          id={`${teamMember.id}-team-member`}
                          checked={value?.includes(teamMember.id)}
                          onCheckedChange={(checked) => {
                            onCheckedChange(checked, onChange, teamMember);
                          }}
                        />
                        <span className="text-base text-ebony opacity-50 cursor-pointer font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {`${teamMember.first_name} ${teamMember.last_name}`}
                        </span>
                      </label>
                      {index < array.length - 1 && <Separator />}
                    </li>
                  );
                })}
              </>
            );
          }}
        />
      </ul>

      <div className={'input-container buttons'}>
        <Button
          variant="pill-green"
          size="pill"
          type="submit"
          form="add-team-member-list-form"
          disabled={!teamMembersValue.length}
        >
          Add User
        </Button>
      </div>
    </form>
  );
};
